/**
 * Heartbeat spend budgets, as the user sees them
 * (docs/specs/2026-09-22-heartbeat-token-safety.md).
 *
 * Two announcements: a task that has spent its autonomous budget, and an
 * agent the rolling-24h cap suspended. Both are one-off crossings — the
 * durable surfaces are the kanban badge and agent settings.
 */

import type { WsMessage } from "@/api/websocket";
import type { Task } from "@/api/tasks";
import { resumeHeartbeatToast } from "@/components/toasts/work-toast-actions";
import type { FrameApplyContext } from "./companion-frame-reducer";
import { translate } from "@/i18n";
import { libNoticesMessages } from "@/i18n/messages/lib-notices";

/**
 * Announce a task crossing into needs_attention.
 *
 * `previousTask` is the stored row as it was BEFORE the update — the board
 * re-broadcasts an already-flagged task on every later edit, and only the
 * false → true crossing is news. A task this client never loaded has no
 * crossing to report.
 */
export function announceTaskNeedsAttention(
  previousTask: Task | undefined,
  task: Task,
  ctx: FrameApplyContext,
): void {
  if (!task.needs_attention || !previousTask || previousTask.needs_attention)
    return;
  ctx.addSystemToast({
    id: `task-needs-attention-${task.id}`,
    title: translate(libNoticesMessages, "taskPaused", {
      task: task.title,
      count: task.autonomous_attempts,
    }),
    // A chat reply deliberately does not refill the budget; a task
    // comment or a board move does.
    description: translate(libNoticesMessages, "taskPausedDescription"),
    level: "warning",
  });
}

/**
 * Announce an agent whose rolling-24h heartbeat cap tripped. Resume is one
 * click here and, durably, in agent settings.
 */
export function applyAgentHeartbeatSuspended(
  msg: WsMessage,
  ctx: FrameApplyContext,
): void {
  const d = msg.data as {
    project_agent_id?: string;
    agent_name?: string;
    project_id?: string;
    runs_24h?: number;
  };
  const projectId = d.project_id;
  const projectAgentId = d.project_agent_id;
  if (!projectId || !projectAgentId) return;
  const toastId = `agent-heartbeat-suspended-${projectAgentId}`;
  ctx.addSystemToast(
    () => ({
      id: toastId,
      title: translate(libNoticesMessages, "heartbeatPausedTitle", {
        agent: d.agent_name ?? translate(libNoticesMessages, "agent"),
      }),
      description: translate(libNoticesMessages, "heartbeatPausedDescription", {
        count: d.runs_24h ?? 0,
      }),
      level: "warning",
      actionLabel: translate(libNoticesMessages, "resume"),
      persistent: true,
    }),
    () => resumeHeartbeatToast({ toastId, projectId, projectAgentId }),
  );
}
